/**
 * RecoveryKeyInput Component
 * Base58-masked controlled input for Matrix recovery keys
 * Handles 48-character keys in 4 groups of 12 with auto-spacing
 */

import { useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import './RecoveryKeyInput.css';

// Base58 alphabet excluding 0, O, I, l per Matrix spec
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE58_REGEX = new RegExp(`^[${BASE58_ALPHABET}]+$`);
const GROUP_SIZE = 12;
const NUM_GROUPS = 4;
const TOTAL_CHARS = GROUP_SIZE * NUM_GROUPS;
const MAX_LENGTH_WITH_SPACES = TOTAL_CHARS + (NUM_GROUPS - 1) + 8; // allow up to 59 chars (12*4 + 11 spaces)

/**
 * Normalize a recovery key string to canonical format
 * @param {string} raw - Raw input string
 * @returns {object} Normalized data
 */
export const normalizeRecoveryKey = (raw) => {
  // Remove all spaces and invalid characters
  const cleaned = raw.replace(/\s/g, '').split('').filter(char =>
    BASE58_ALPHABET.includes(char),
  ).join('');

  // Check if the input is in Matrix standard format (groups of 4)
  const trimmedInput = raw.trim().replace(/\s+/g, ' ');
  const inputGroups = trimmedInput.split(' ').filter(g => g.length > 0);
  const isMatrixFormat = inputGroups.length === 12 && inputGroups.every(g => g.length === 4);

  let groups;
  let normalized;

  if (isMatrixFormat && cleaned.length === TOTAL_CHARS) {
    // Preserve Matrix standard format (groups of 4)
    groups = inputGroups;
    normalized = trimmedInput;
  } else {
    // Use groups of 12 for UI display
    groups = [];
    for (let i = 0; i < cleaned.length; i += GROUP_SIZE) {
      groups.push(cleaned.slice(i, i + GROUP_SIZE));
    }
    normalized = groups.join(' ');
  }

  // Check if format is valid (48 characters total)
  const isFormatValid = cleaned.length === TOTAL_CHARS &&
    cleaned.split('').every(char => BASE58_REGEX.test(char));

  return {
    normalized,
    groups,
    isFormatValid,
    charCount: cleaned.length,
  };
};

/**
 * Validate that a string is a valid recovery key format
 * @param {string} key - Key to validate
 * @returns {boolean} True if valid format
 */
export const isValidRecoveryKeyFormat = (key) => {
  const { isFormatValid } = normalizeRecoveryKey(key);
  return isFormatValid;
};

const RecoveryKeyInput = ({
  value = '',
  onChange,
  disabled = false,
  autoFocus = false,
  id,
  className,
  inputRef: externalRef,
  'data-testid': dataTestId = 'recovery-key-input',
  'aria-label': ariaLabel = 'Recovery key',
  'aria-describedby': ariaDescribedBy,
  placeholder = 'XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX',
  onKeyDown,
  error = false,
}) => {
  const internalRef = useRef(null);
  const inputElementRef = externalRef || internalRef;

  // Calculate current format validity
  const formatInfo = useMemo(() => normalizeRecoveryKey(value), [value]);

  /**
   * Format input value with auto-spacing
   */
  const formatValue = useCallback((input) => {
    // Check if input is in Matrix standard format (groups of 4)
    const trimmed = input.trim().replace(/\s+/g, ' ');
    const groups = trimmed.split(' ').filter(g => g.length > 0);
    const isMatrixFormat = groups.length === 12 && groups.every(g => g.length === 4);

    // Remove all spaces first
    const cleanInput = input.replace(/\s/g, '');

    // Filter out invalid characters
    const validChars = cleanInput.split('').filter(char =>
      BASE58_ALPHABET.includes(char),
    );

    // Limit to max characters
    const limited = validChars.slice(0, TOTAL_CHARS).join('');

    // Preserve Matrix format if detected and complete, otherwise use groups of 12
    if (isMatrixFormat && limited.length === TOTAL_CHARS) {
      // Rebuild with Matrix format spacing
      const matrixGroups = [];
      for (let i = 0; i < limited.length; i += 4) {
        matrixGroups.push(limited.slice(i, i + 4));
      }
      return matrixGroups.join(' ');
    } else {
      // Add spaces every 12 characters for UI display
      const formatted = limited.match(/.{1,12}/g)?.join(' ') || '';
      return formatted;
    }
  }, []);

  /**
   * Handle input change with formatting
   */
  const handleChange = useCallback((e) => {
    const rawValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Format the value
    const formatted = formatValue(rawValue);

    // Calculate new cursor position
    let newCursorPos = cursorPos;

    // If we added a space before the cursor, move cursor forward
    if (formatted.length > value.length && formatted[cursorPos - 1] === ' ') {
      newCursorPos = cursorPos + 1;
    }

    // Normalize and get metadata
    const meta = normalizeRecoveryKey(formatted);

    // Call onChange with normalized value and metadata
    if (onChange) {
      onChange(formatted, {
        raw: rawValue,
        isFormatValid: meta.isFormatValid,
        charCount: meta.charCount,
      });
    }

    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      if (inputElementRef.current) {
        inputElementRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  }, [value, onChange, formatValue, inputElementRef]);

  /**
   * Handle paste events
   */
  const handlePaste = useCallback((e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');
    const formatted = formatValue(pastedText);

    const meta = normalizeRecoveryKey(formatted);

    if (onChange) {
      onChange(formatted, {
        raw: pastedText,
        isFormatValid: meta.isFormatValid,
        charCount: meta.charCount,
      });
    }
  }, [onChange, formatValue]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((e) => {
    // Allow standard keys
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                        'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (allowedKeys.includes(e.key)) {
      if (onKeyDown) {
        onKeyDown(e);
      }
      return;
    }

    // Allow Ctrl/Cmd combinations
    if (e.ctrlKey || e.metaKey) {
      if (onKeyDown) {
        onKeyDown(e);
      }
      return;
    }

    // Block invalid characters
    if (e.key.length === 1 && !BASE58_ALPHABET.includes(e.key)) {
      e.preventDefault();
      return;
    }

    // Block input if already at max length (excluding spaces)
    const currentChars = value.replace(/\s/g, '').length;
    if (currentChars >= TOTAL_CHARS && e.key.length === 1) {
      e.preventDefault();
      return;
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  }, [value, onKeyDown]);

  const isInvalid = error || (value.length > 0 && !formatInfo.isFormatValid);

  return (
    <div className={`recovery-key-input-wrapper ${className || ''}`}>
      <input
        ref={inputElementRef}
        id={id}
        type="text"
        className={`recovery-key-input ${isInvalid ? 'recovery-key-input-invalid' : ''}`}
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode="text"
        maxLength={MAX_LENGTH_WITH_SPACES}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={isInvalid}
      />
      <div className="recovery-key-input-status">
        <span className="recovery-key-char-count">
          {formatInfo.charCount} / {TOTAL_CHARS}
        </span>
        {formatInfo.isFormatValid && (
          <ion-icon
            name="checkmark-circle"
            className="recovery-key-valid-icon"
            aria-label="Valid format"
          />
        )}
      </div>
    </div>
  );
};

RecoveryKeyInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  'data-testid': PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  placeholder: PropTypes.string,
  onKeyDown: PropTypes.func,
  error: PropTypes.bool,
};

export default RecoveryKeyInput;